import { useState } from 'react';

import { css } from '@compiled/react';

import CheckIcon from '@atlaskit/icon/core/check-mark';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import { Box, Inline, Stack, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { useDeviceInfo } from '@/utils/use-device-info';

import { ChromeIcon } from '../assets/ChromeIcon';
import { DesktopIcon } from '../assets/DesktopIcon';
import { useIsLoomExtensionInstalled } from '../utilities/use-is-loom-extension-installed';

import { DownloadMacHelperModal } from './download-mac-helper-modal';
import { DownloadMacOptionsDropdown } from './download-mac-options-dropdown';
import { LoomOnboardingButton } from './loom-onboarding-button';
import { LoomOnboardingHeading } from './loom-onboarding-heading';
import { LoomOnboardingText } from './loom-onboarding-text';

import type { Recorder } from '../utilities/types';

const ChromeText = {
	title: 'Chrome extension',
	subtitle: 'Installs fast for a quick start',
	valueProps: ['Free', 'Personalized recording canvas', 'Camera bubble backgrounds', 'Blur tool'],
};

export const ChromeInstallLink =
	'https://chromewebstore.google.com/detail/loom-%E2%80%93-screen-recorder-sc/liecbddmkiiihnedobmlmillhodjkdmb';

const DesktopText = {
	title: 'Desktop app',
	subtitle: 'Higher quality video and additional features ',
	valueProps: [
		'Free',
		'Personalized recording canvas',
		'Camera bubble backgrounds & frames',
		'Draw tool',
		'Speaker notes',
		'Highlight mouse clicks',
		'Record in HD (up to 4k)',
	],
};

// Uses a raw 16px border-radius to preserve the original design. The previous
// `border.radius.400` token was deprecated by ADS with no direct token
// replacement (see tokens CHANGELOG migration guide), so we drop into a
// Compiled `css` block on a wrapper element because xcss `borderRadius` only
// accepts a fixed set of tokens.
const cardCss = css({
	position: 'relative',
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	gap: token('space.300'),
	backgroundColor: token('elevation.surface'),
	borderColor: token('color.border'),
	borderStyle: 'solid',
	borderWidth: token('border.width'),
	borderRadius: '16px',
	padding: token('space.500'),
	width: '100%',
	maxWidth: '24rem',
	marginInline: 'auto',
});

const valuePropListStyles = xcss({
	alignSelf: 'stretch',
	padding: 'space.0',
	margin: 'space.0',
	textAlign: 'left',
});

const buttonWrapperStyles = xcss({
	marginBlockStart: 'auto',
	alignSelf: 'stretch',
	marginInline: 'auto',
	width: '100%',
	position: 'relative',
});

const subtitleWrapperStyles = xcss({
	marginBlockStart: '0.5em',
});

// Download-button label layout: text centered with the chevron pushed to
// the inline-end. The outer <span> fills the button width so the chevron
// lands flush against the right padding regardless of the label width.
const downloadLabelCss = css({
	display: 'flex',
	alignItems: 'center',
	width: '100%',
});

const downloadLabelTextCss = css({
	flex: 1,
	textAlign: 'center',
	// Reserve the chevron's visual footprint on the opposite side so
	// "Download" stays optically centered within the button.
	paddingInlineStart: '16px',
});

const downloadLabelChevronCss = css({
	display: 'inline-flex',
	alignItems: 'center',
	marginInlineStart: 'auto',
	color: 'currentColor',
});

type RecorderCardProps = {
	recorder: 'chrome' | 'desktop';
	handleClick: (recorder: Recorder) => void;
};

export const RecorderCard = ({ recorder, handleClick }: RecorderCardProps) => {
	const [isMacDropDownOpen, setIsMacDropDownOpen] = useState(false);
	const [isWhichVersionModalOpen, setIsWhichVersionModalOpen] = useState(false);

	const handleWhichVersionModalClose = (selectedRecorder?: Recorder) => {
		setIsWhichVersionModalOpen(false);
		setIsMacDropDownOpen(false);
		if (selectedRecorder) {
			handleClick(selectedRecorder);
		}
	};

	const copy = recorder === 'chrome' ? ChromeText : DesktopText;
	const isChromeExtensionInstalled = useIsLoomExtensionInstalled();
	const { isMac } = useDeviceInfo();

	const handleDownloadInstallClick = () => {
		if (recorder === 'chrome') {
			// Open the store in a new tab first so the user-gesture is still
			// intact (popup blockers). Provisioning / redirects stay on this tab.
			window.open(ChromeInstallLink, '_blank', 'noopener,noreferrer');
			handleClick('extension');
			return;
		}
		if (!isMac) {
			handleClick('windows');
			return;
		}
		// Mac + desktop: clicking anywhere on the Download button opens the
		// version-picker dropdown. The chevron inside the button is purely a
		// visual affordance for the dropdown — it is not a separate click
		// target.
		setIsMacDropDownOpen(true);
	};

	// Mac + desktop renders the Download button with a chevron affordance
	// to signal that it opens a dropdown.
	const showDownloadChevron = recorder === 'desktop' && isMac;

	return (
		<div css={cardCss}>
			{recorder === 'chrome' ? (
				<ChromeIcon isLargeImage={false} />
			) : (
				<DesktopIcon isLargeImage={false} />
			)}
			<Box>
				<LoomOnboardingHeading level={3}>{copy.title}</LoomOnboardingHeading>
				<Box xcss={subtitleWrapperStyles}>
					<LoomOnboardingText align="center">{copy.subtitle}</LoomOnboardingText>
				</Box>
			</Box>
			<Stack space="space.100" as="ul" xcss={valuePropListStyles}>
				{copy.valueProps.map((item) => (
					<Inline key={item} space="space.100" alignBlock="start" as="li">
						<CheckIcon label="" color={token('color.icon.subtle')} />
						<LoomOnboardingText inline>{item}</LoomOnboardingText>
					</Inline>
				))}
			</Stack>
			<Box xcss={buttonWrapperStyles}>
				<LoomOnboardingButton
					appearance={recorder === 'chrome' && !isChromeExtensionInstalled ? 'primary' : 'default'}
					fullWidth
					isActive={isMacDropDownOpen}
					onClick={handleDownloadInstallClick}
					aria-haspopup={showDownloadChevron ? 'menu' : undefined}
					aria-expanded={showDownloadChevron ? isMacDropDownOpen : undefined}
				>
					{showDownloadChevron ? (
						<span css={downloadLabelCss}>
							<span css={downloadLabelTextCss}>Download</span>
							<span css={downloadLabelChevronCss}>
								<ChevronDownIcon label="" color="currentColor" />
							</span>
						</span>
					) : recorder === 'chrome' ? (
						isChromeExtensionInstalled ? (
							'Currently installed'
						) : (
							'Install'
						)
					) : (
						'Download'
					)}
				</LoomOnboardingButton>
				{isMacDropDownOpen && (
					<DownloadMacOptionsDropdown
						handleClick={handleClick}
						onNeedHelp={() => setIsWhichVersionModalOpen(true)}
						onClose={() => setIsMacDropDownOpen(false)}
					/>
				)}
			</Box>
			<DownloadMacHelperModal
				isOpen={isWhichVersionModalOpen}
				handleClose={handleWhichVersionModalClose}
			/>
		</div>
	);
};
